import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  inject,
  input,
  model,
  signal,
  viewChildren,
} from '@angular/core';
import { StepperOption } from './stepper-options.interface';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { BadgeComponent } from '../badge/badge.component';
import { AvatarComponent } from '../avatar/avatar.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgTemplateOutlet } from '@angular/common';
import {
  SolidArrowDownIconComponent,
  CircleArrowLeftIconComponent,
  StepStatusIconComponent,
} from '@kuiper/icons';
import { TrimTooltipDirective } from '../../directives/trim-tooltip/trim-tooltip.directive';

@Component({
  selector: 'lib-stepper',
  imports: [
    TooltipComponent,
    BadgeComponent,
    AvatarComponent,
    TranslocoPipe,
    NgTemplateOutlet,
    SolidArrowDownIconComponent,
    CircleArrowLeftIconComponent,
    StepStatusIconComponent,
    TrimTooltipDirective,
  ],
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StepperComponent implements OnInit {
  private readonly elementRef = inject(ElementRef);

  stepperLabel = viewChildren<ElementRef>('stepperLabel');
  options = input.required<StepperOption[]>();
  isVertical = input<boolean>(false);
  isCentered = input<boolean>(true);
  assigneeTemplate = input<TemplateRef<unknown> | null>(null);
  stepperId = input<string>('');
  isShowParallelSteps = model<boolean>(false);
  clickedStep = model<number>(0);

  /* Scroll position in vw units */
  scrollPosition = signal<number>(0);
  index = signal<number>(0);
  visibleOptions = signal<number>(4); // Default visible options count
  maxIndex = signal<number>(0); // Maximum index value (calculated dynamically)
  firstOptionWidth = 10; // Width of first option in vw
  otherOptionWidth = 19; // Width of other options in vw (matches template 19vw)

  descriptionTemplate = input<TemplateRef<unknown> | null>(null);

  ngOnInit() {
    if (!this.isVertical()) {
      // Calculate visible options based on container width after view is initialized
      setTimeout(() => {
        this.calculateVisibleOptions();
        this.updateMaxIndex();

        // Add resize listener to recalculate on window resize
        window.addEventListener('resize', () => {
          this.calculateVisibleOptions();
          this.updateMaxIndex();
        });
      }, 100);
    }
  }

  private getRemBase(): number {
    return (
      Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize || '16'
      ) || 16
    );
  }

  calculateVisibleOptions() {
    const container = this.elementRef.nativeElement.querySelector(
      '.horizontal-container'
    );
    if (container) {
      const containerWidthPx = container.offsetWidth - 288; // Side menu width in px;
      // 1vw = window.innerWidth / 100 (actual CSS vw definition)
      const vwToPx = window.innerWidth / 100;
      const firstOptionWidthPx = this.firstOptionWidth * vwToPx;
      const otherOptionWidthPx = this.otherOptionWidth * vwToPx;

      // Calculate how many options fit in the container
      const visibleCount = Math.floor(
        (containerWidthPx - firstOptionWidthPx) / otherOptionWidthPx
      );
      this.visibleOptions.set(Math.max(1, visibleCount));

      // Update max index whenever visible options change
      this.updateMaxIndex();
    }
  }

  getStepperLabelWidth(index: number): number {
    const labels = this.stepperLabel();
    if (labels && labels.length > index && labels[index]?.nativeElement) {
      // Convert pixels to vw units
      const pixelWidth = labels[index].nativeElement.offsetWidth || 0;
      const vwWidth = (pixelWidth / window.innerWidth) * 100;
      return vwWidth;
    }
    return 0;
  }

  updateMaxIndex() {
    // Calculate the maximum possible index based on total options and visible options
    const totalOptions = this.options().length;
    const visibleCount = this.visibleOptions();
    this.maxIndex.set(Math.max(0, totalOptions - visibleCount));
  }

  isRightScrollDisabled = () => {
    // Right scroll should be disabled when we've reached the maximum index
    return this.index() >= this.maxIndex();
  };

  showSteps = (option: StepperOption, index: number) => {
    this.clickedStep.set(index + 1);
    if (option.parallelSteps && option.parallelSteps.length > 1) {
      this.isShowParallelSteps.set(!this.isShowParallelSteps());
      return option.parallelSteps;
    }
    return [];
  };

  scrollLeft() {
    const container: HTMLElement | null = document.querySelector('.horizontal');
    if (container) {
      this.index.set(this.index() - 1);
      let scrollDistanceVw = 0;

      if (this.index() === 0) {
        // Scrolling back to first position: undo the narrow first-option scroll
        scrollDistanceVw = this.firstOptionWidth;
      } else {
        // Regular scroll - one option width
        scrollDistanceVw = this.otherOptionWidth;
      }

      this.scrollPosition.set(this.scrollPosition() + scrollDistanceVw);
      container.style.insetInlineStart = `${this.scrollPosition()}vw`;
    }
  }

  scrollRight() {
    const container: HTMLElement | null = document.querySelector('.horizontal');
    if (container) {
      this.index.set(this.index() + 1);
      let scrollDistanceVw = 0;

      if (this.index() === 1) {
        // First scroll: skip past the narrow first option
        scrollDistanceVw = this.firstOptionWidth;
      } else {
        // Regular scroll - one option width
        scrollDistanceVw = this.otherOptionWidth;
      }

      this.scrollPosition.set(this.scrollPosition() - scrollDistanceVw);
      container.style.insetInlineStart = `${this.scrollPosition()}vw`;
    }
  }
}
